// =============================================================================
// MeuDriver — Relatório mensal do instrutor (I4)
// Layout otimizado para impressão. Usa window.print() para gerar PDF.
// =============================================================================

import { Printer, Star, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { InstructorReportData } from '@/hooks/useInstructorReport'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string) {
  try { return format(parseISO(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) }
  catch { return iso }
}

function monthLabel(month: string) {
  const [y, m] = month.split('-').map(Number)
  return format(new Date(y, m - 1, 1), "MMMM 'de' yyyy", { locale: ptBR })
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InstructorMonthlyReportProps {
  data: InstructorReportData | undefined
  isLoading: boolean
  month: string
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function InstructorMonthlyReport({ data, isLoading, month }: InstructorMonthlyReportProps) {
  function handlePrint() {
    window.print()
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!data) return null

  const monthStr = monthLabel(month)

  return (
    <>
      {/* Botão de impressão — oculto na impressão */}
      <div className="flex justify-end mb-4 print:hidden">
        <Button
          onClick={handlePrint}
          className="gap-2 bg-brand-600 hover:bg-brand-700"
          data-testid="report-print-btn"
        >
          <Printer className="h-4 w-4" />
          Imprimir / Salvar PDF
        </Button>
      </div>

      {/* Área de impressão */}
      <div
        id="instructor-report"
        className={cn(
          'bg-white rounded-2xl border border-gray-200 overflow-hidden',
          'print:border-none print:rounded-none print:shadow-none',
        )}
      >
        {/* Cabeçalho */}
        <div className="bg-brand-600 text-white px-8 py-6 print:py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-2xl font-extrabold tracking-tight print:text-xl">MeuDriver</p>
              <p className="text-brand-200 text-sm mt-0.5">Relatório Mensal de Desempenho</p>
            </div>
            <div className="text-right">
              <p className="font-semibold capitalize">{monthStr}</p>
              {data.instructorName && (
                <p className="text-brand-200 text-sm">{data.instructorName}</p>
              )}
            </div>
          </div>
        </div>

        {/* Resumo */}
        <div className="px-8 py-6 border-b border-gray-100 print:py-4">
          <h2 className="text-base font-bold text-gray-900 mb-4">Resumo do mês</h2>
          <div className="grid grid-cols-3 gap-4">
            <SummaryBox
              label="Aulas realizadas"
              value={String(data.totalLessons)}
              icon={<Calendar className="h-4 w-4 text-brand-600" />}
            />
            <SummaryBox
              label="Receita líquida"
              value={formatCurrency(data.totalRevenue)}
              icon={<span className="text-sm font-bold text-teal-600">R$</span>}
            />
            <SummaryBox
              label="Média de avaliação"
              value={data.avgRating != null ? data.avgRating.toFixed(1) + ' / 5.0' : '—'}
              icon={<Star className="h-4 w-4 text-yellow-500" />}
            />
          </div>
        </div>

        {/* Tabela de aulas */}
        <div className="px-8 py-6 border-b border-gray-100 print:py-4">
          <h2 className="text-base font-bold text-gray-900 mb-4">Aulas realizadas</h2>
          {data.bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma aula concluída neste mês.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 text-left font-semibold text-gray-600 pr-4">Data e horário</th>
                    <th className="pb-2 text-left font-semibold text-gray-600 pr-4">Aluno</th>
                    <th className="pb-2 text-right font-semibold text-gray-600 pr-4">Duração</th>
                    <th className="pb-2 text-right font-semibold text-gray-600">Valor líquido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.bookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-gray-50/50 print:hover:bg-transparent">
                      <td className="py-2.5 pr-4 text-gray-700">{fmtDate(booking.scheduled_at)}</td>
                      <td className="py-2.5 pr-4 text-gray-900">{booking.student_name ?? '—'}</td>
                      <td className="py-2.5 pr-4 text-right text-gray-600">{booking.duration_minutes} min</td>
                      <td className="py-2.5 text-right font-semibold text-teal-700">
                        {booking.instructor_payout != null ? formatCurrency(booking.instructor_payout) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200">
                    <td colSpan={3} className="pt-3 text-sm font-bold text-gray-700">Total</td>
                    <td className="pt-3 text-right font-extrabold text-teal-700">
                      {formatCurrency(data.totalRevenue)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Avaliações recebidas */}
        {data.reviews.length > 0 && (
          <div className="px-8 py-6 border-b border-gray-100 print:py-4">
            <h2 className="text-base font-bold text-gray-900 mb-4">Avaliações recebidas</h2>
            <div className="space-y-3">
              {data.reviews.map((review) => (
                <div key={review.id} className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 print:border-gray-200">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={cn(
                            'h-4 w-4',
                            s <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200',
                          )}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{review.rating}/5</span>
                    {review.student_name && (
                      <span className="text-xs text-muted-foreground ml-auto">{review.student_name}</span>
                    )}
                  </div>
                  {review.comment && (
                    <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rodapé */}
        <div className="px-8 py-4 bg-gray-50 print:bg-transparent">
          <p className="text-xs text-muted-foreground text-center">
            MeuDriver — Marketplace de Instrutores de Direção &nbsp;|&nbsp;
            Emitido em {format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
          </p>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// SummaryBox interno
// ---------------------------------------------------------------------------

interface SummaryBoxProps {
  label: string
  value: string
  icon: React.ReactNode
}

function SummaryBox({ label, value, icon }: SummaryBoxProps) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 text-center print:border-gray-200">
      <div className="flex justify-center mb-1">{icon}</div>
      <p className="text-xl font-extrabold text-gray-900">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  )
}
